import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import './Pagination.scss';

export interface PaginationLabels {
  prev?: string;       // default 'Prev'
  next?: string;       // default 'Next'
  prevAria?: string;   // default 'Previous page'
  nextAria?: string;   // default 'Next page'
  rows?: string;       // default 'Rows'
  indicator?: (page: number, totalPages?: number) => ReactNode; // default below
}

export type PaginationProps = (
  | {
      mode: 'hasMore';
      page: number;
      pageSize: number;
      hasMore: boolean;
      onPageChange: (page: number) => void;
      disabled?: boolean;
      className?: string;
    }
  | {
      mode: 'total';
      page: number;
      pageSize: number;
      total: number;
      onPageChange: (page: number) => void;
      pageSizeOptions?: number[];
      onPageSizeChange?: (n: number) => void;
      disabled?: boolean;
      className?: string;
    }
) & { labels?: PaginationLabels };

export function Pagination(props: PaginationProps) {
  const { mode, page, pageSize, onPageChange, disabled = false, className = '', labels } = props;

  const prevDisabled = disabled || page <= 1;

  let nextDisabled: boolean;
  let indicator: ReactNode;

  if (mode === 'hasMore') {
    nextDisabled = disabled || !props.hasMore;
    indicator = labels?.indicator
      ? labels.indicator(page, undefined)
      : `Page ${page}`;
  } else {
    const totalPages = Math.max(1, Math.ceil(props.total / pageSize));
    nextDisabled = disabled || page >= totalPages;
    indicator = labels?.indicator
      ? labels.indicator(page, totalPages)
      : `Page ${page} of ${totalPages}`;
  }

  const showPageSize =
    mode === 'total' &&
    Array.isArray(props.pageSizeOptions) &&
    props.pageSizeOptions.length > 0 &&
    typeof props.onPageSizeChange === 'function';

  return (
    <div className={`pagination ${className}`}>
      {showPageSize && mode === 'total' && (
        <div className="pagination__page-size">
          <label className="pagination__page-size-label" htmlFor="pagination-page-size">
            {labels?.rows ?? 'Rows'}
          </label>
          <select
            id="pagination-page-size"
            className="pagination__page-size-select"
            value={pageSize}
            disabled={disabled}
            onChange={(e) => props.onPageSizeChange?.(Number(e.target.value))}
          >
            {props.pageSizeOptions?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      <span className="pagination__indicator">{indicator}</span>

      <div className="pagination__controls">
        <Button
          variant="ghost"
          size="sm"
          disabled={prevDisabled}
          onClick={() => onPageChange(page - 1)}
          aria-label={labels?.prevAria ?? 'Previous page'}
        >
          <ChevronLeft size={16} />
          {labels?.prev ?? 'Prev'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={nextDisabled}
          onClick={() => onPageChange(page + 1)}
          aria-label={labels?.nextAria ?? 'Next page'}
        >
          {labels?.next ?? 'Next'}
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}
